import { FC } from 'react';
import { useQuery } from '@apollo/client';
import { DateTime } from 'luxon';
import { Card as _Card, CardHeader } from '@material-ui/core';
import styled from '@emotion/styled';

import {
  KaizenChartForHooks,
  ChartBreakdownRequest,
  FrequencyValue,
  Chart,
  RequestVariables,
} from '@kaizenplatform/kaizen-chartjs';

import {
  GetChartQueryVariables,
  GetChartDocument,
  ChartScopeInput,
  GoogleSearchConsoleSite,
} from '../../../../generated/graphql';
import _ChartContainer from '../ChartBase/ChartContainer';

type Props = {
  linkedSite: GoogleSearchConsoleSite;
};

const GoogleSearchConsoleOverviewChart: FC<Props> = ({ linkedSite }) => {
  const overviewChartId = linkedSite.overview_resource_key ?? '';
  const from = DateTime.utc().minus({ years: 1 }).startOf('month').toISO();
  const to = DateTime.utc().endOf('month').toISO();
  const initialFrequency = 'WEEKLY' as FrequencyValue;
  const siteBreakdown = new ChartBreakdownRequest({
    breakdown: 'site',
    filter: [linkedSite.site_url ?? ''],
  });
  const variablesForChart = {
    id: overviewChartId,
    identifierType: 'resource_key',
    scope: {
      from: from,
      to: to,
      frequency: initialFrequency,
    } as ChartScopeInput,
    breakdowns: [siteBreakdown],
  };

  const {
    data,
    loading,
    refetch: refetchChart,
  } = useQuery<{ chart: Chart }, GetChartQueryVariables>(GetChartDocument, {
    skip: !linkedSite,
    variables: variablesForChart,
    notifyOnNetworkStatusChange: true,
    context: {
      clientName: 'dataplatform',
    },
  });

  const refetch = (variables: RequestVariables) => {
    const _variables = { ...variables };
    if (_variables.breakdowns) _variables.breakdowns.push(siteBreakdown);

    refetchChart(_variables);
  };

  return (
    <Card>
      <CardHeader title="検索回数トレンド" />
      <ChartWrapper>
        <ChartContainer>
          <KaizenChartForHooks
            chartData={data?.chart || null}
            identifier={{ id: overviewChartId, identifierType: 'resource_key' }}
            scope={{
              from,
              to,
              frequency: initialFrequency,
            }}
            frequencies={data?.chart?.frequencies ?? undefined}
            refetch={refetch}
            loading={loading}
          />
        </ChartContainer>
      </ChartWrapper>
    </Card>
  );
};

const Card = styled(_Card)(`
  .MuiCardHeader-root {
    padding: 24px;
  }
  .MuiCardHeader-title {
    color: #4C566C;
    font-weight: 700;
  }
`);

const ChartWrapper = styled('div')({
  position: 'relative',
  padding: '0 24px 16px',
});

const ChartContainer = styled(_ChartContainer)(`
  .MuiTableCell-root {
    font-size: 12px;
    max-width: 400px;
  }
  .MuiTableCell-head {
    font-weight: bold;
  }
  .MuiToolbar-root {
    min-height: 48px;
  }
`);

export default GoogleSearchConsoleOverviewChart;
